// src/utils/displayName.js
//
// How a person is addressed, in one place.
//
// The rule differs by who's being named and who's reading:
//
//   an educator, to anyone     "Mr. Eric"        title + last name
//   a student, to themselves   "Martin"          first name
//   a class, to their student  "Mr. Eric's Math class"
//   a class, to a stranger     "Hey Martin!"
//
// Scattering that across the dashboards and the portal would guarantee they
// drift — one page says "Mr. Martin Eric", another "Martin Eric", a third
// "Mr. Eric". So the phrasing lives here and the pages just call it.
//
// A note on the field name: `sirName` is what this codebase has always called
// the title, and it's already written by AdminRoles, createUserWithStudents,
// progressMirror, PurchaseOrders and CustomContent. It's a misnomer — it's an
// honorific, not a surname — but renaming it now would mean a data migration
// across all of those for no user-visible gain. Kept as-is deliberately.

import { subjectLabel } from "../data/subjects";

// Matches the list in AdminRoles.js, with Dr. and Mx. added. "N/A" is kept
// because existing records use it, and it's treated as no title at all.
export const TITLE_OPTIONS = [
  "Mr.",
  "Mrs.",
  "Ms.",
  "Miss",
  "Dr.",
  "Mx.",
  "Prof.",
  "Coach",
  "Principal",
  "Vice Principal",
  "Assistant Principal",
];

const NO_TITLE = new Set(["", "n/a", "na", "none", "-"]);

/**
 * The title, or "" when there isn't a usable one.
 */
export function title(profile = {}) {
  const raw = String(profile.sirName || "").trim();
  return NO_TITLE.has(raw.toLowerCase()) ? "" : raw;
}

/**
 * How an educator is addressed: "Mr. Eric".
 *
 * Falls back through what's available rather than rendering a gap — an
 * account with no title reads as "Eric", one with no last name as their first
 * name, and one with nothing at all as "your teacher", which is still a
 * sentence.
 */
export function formalName(profile = {}, fallback = "your teacher") {
  const t = title(profile);
  const last = String(profile.lastName || "").trim();
  const first = String(profile.firstName || "").trim();

  if (t && last) return `${t} ${last}`;
  if (last) return last;
  if (t && first) return `${t} ${first}`;
  if (first) return first;

  // Older records only have a combined name. Two field names, because two
  // collections spell it differently: users/{uid} stores `name`, while
  // educatorDirectory/{code} entries written before sirName and lastName
  // were stored separately only have `displayName`.
  //
  // Without this line those legacy entries fall all the way through to the
  // fallback, and a student whose teacher signed up last month gets "Hey
  // Aiden!" instead of their class name — the one case where the greeting
  // silently does the wrong thing rather than visibly breaking.
  const combined = String(profile.name || profile.displayName || "").trim();
  if (combined) return combined;

  return fallback;
}

/**
 * How a student is addressed: their first name.
 */
export function friendlyName(profile = {}, fallback = "there") {
  const first = String(profile.firstName || "").trim();
  if (first) return first;

  const combined = String(profile.name || "").trim();
  if (combined) return combined.split(/\s+/)[0];

  return fallback;
}

/**
 * Possessive form. "Mr. Eric" -> "Mr. Eric's", "Mr. Jones" -> "Mr. Jones'".
 *
 * Worth the four lines: "Mr. Jones's Math class" is the kind of small wrong
 * thing a teacher notices on a screen their class is looking at.
 */
export function possessive(name) {
  const clean = String(name || "").trim();
  if (!clean) return "";
  return /s$/i.test(clean) ? `${clean}'` : `${clean}'s`;
}

/**
 * "Math class", "Social Studies class".
 *
 * "Other" is the exception — "Mr. Eric's Other class" reads like a mistake,
 * so that subject just gets "class".
 */
export function classLabel(subject) {
  const value = String(subject || "").toLowerCase();
  if (!value || value === "other") return "class";
  return `${subjectLabel(value)} class`;
}

/**
 * The dashboard heading.
 *
 *   educator  "Welcome to Mr. Eric's Dashboard"
 *   student   "Welcome to Martin's Dashboard"
 */
export function dashboardGreeting(profile = {}, { isEducator = false } = {}) {
  const who = isEducator
    ? formalName(profile, "your")
    : friendlyName(profile, "your");

  // formalName's fallback is already possessive-ish ("your"), so don't
  // double up on it.
  if (who === "your") return "Welcome to your Dashboard";

  return `Welcome to ${possessive(who)} Dashboard`;
}

/**
 * The subject-page heading.
 *
 * @param {object}  opts.viewer    the signed-in person's profile
 * @param {object}  opts.educator  the educator who owns this class, if known
 * @param {boolean} opts.isEducator is the viewer the educator themselves
 * @param {string}  opts.subject   subject token from the URL
 *
 * A student only gets the named-class greeting when they actually share the
 * educator's code. Someone learning on their own has no teacher to be
 * welcomed by, and inventing one would be worse than greeting them directly.
 */
export function classGreetingParts({
  viewer = {},
  educator = null,
  isEducator = false,
  subject = "",
} = {}) {
  const label = classLabel(subject);

  // An educator looking at their own class. No "Hey" — a teacher opening
  // their Math page doesn't need to be greeted by first name in front of
  // the room, and the class name is the useful half of the sentence.
  if (isEducator) {
    const me = formalName(viewer, "");
    return {
      hey: "",
      main: me
        ? `Welcome to ${possessive(me)} ${label}`
        : `Welcome to your ${label}`,
    };
  }

  // A student. Their own name comes FIRST, then the class.
  //
  //   "Hey Aiden! Welcome to Mr. Eric's Science class"
  //
  // The personal half is what makes a child feel addressed; the class half
  // is what tells them they're in the right place. Leading with the teacher
  // alone made the page feel like it belonged to someone else.
  const hey = `Hey ${friendlyName(viewer)}!`;

  if (educator) {
    const teacher = formalName(educator, "");
    if (teacher) {
      return { hey, main: `Welcome to ${possessive(teacher)} ${label}` };
    }
  }

  // No linked educator: still name the class, just don't attribute it to a
  // teacher this child has never met.
  return { hey, main: `Welcome to your ${label}` };
}

/**
 * The same sentence as one string, for anywhere that can't render the two
 * parts separately.
 */
export function classGreeting(opts = {}) {
  const { hey, main } = classGreetingParts(opts);
  return [hey, main].filter(Boolean).join(" ");
}

export default formalName;
